import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { useSiteSettings } from "@/lib/settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const settings = useSiteSettings();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => { setPost(res.data); setStatus("ok"); })
      .catch(() => setStatus("missing"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0f1117]">
        <Navbar />
        <p className="pt-32 text-center text-slate-500 font-body">Loading article…</p>
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="blog-post-missing">
        <Seo title="Article not found" description="This article could not be found." noindex />
        <Navbar />
        <div className="pt-32 pb-20 text-center px-6">
          <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-white mb-4">Article not found</h1>
          <Link to="/blog">
            <Button className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="back-to-blog">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to the blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isHtml = /<\/?[a-z][\s\S]*>/i.test(post.content || "");

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        image={post.cover_image || "/logo.png"}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image || undefined,
          mainEntityOfPage: { "@type": "WebPage", "@id": `${window.location.origin}/blog/${post.slug}` },
          author: {
            "@type": "Person",
            name: post.author || "Yahav Ben Yosef",
            jobTitle: "Founder & English tutor, Tutors Melbourne",
            description: "Scored a raw 50 in VCE English and a 99+ ATAR.",
            url: "https://tutorsmelbourne.com/#about",
            worksFor: { "@type": "EducationalOrganization", name: "Tutors Melbourne" },
          },
          publisher: {
            "@type": "EducationalOrganization",
            name: "Tutors Melbourne",
            logo: { "@type": "ImageObject", url: `${window.location.origin}/logo.png` },
          },
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          inLanguage: "en-AU",
        }}
      />
      <Navbar />

      <article className="pt-28 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-body text-brand-purple dark:text-brand-purple-light hover:underline mb-6" data-testid="blog-post-back">
            <ArrowLeft className="h-3.5 w-3.5" /> All articles
          </Link>

          {post.category && (
            <Badge className="bg-brand-gold/10 text-brand-gold font-body font-semibold px-3 py-1 rounded-full mb-4">{post.category}</Badge>
          )}
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-8">
            By {post.author || "Yahav Ben Yosef"} — raw 50 VCE English, 99+ ATAR
            {post.published_at ? ` · ${new Date(post.published_at).toLocaleDateString("en-AU", { dateStyle: "medium" })}` : ""}
          </p>

          {post.cover_image && (
            <img src={post.cover_image} alt={post.title} className="w-full max-h-[420px] object-cover rounded-2xl mb-10" />
          )}

          {isHtml ? (
            <div
              className="blog-content font-body text-slate-700 dark:text-slate-300 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="blog-post-content"
            />
          ) : (
            <div className="font-body text-slate-700 dark:text-slate-300 leading-relaxed space-y-4 whitespace-pre-wrap" data-testid="blog-post-content">
              {post.content}
            </div>
          )}

          {post.external_url && (
            <a href={post.external_url} target="_blank" rel="noreferrer nofollow" className="inline-flex items-center gap-1.5 text-sm font-body text-brand-purple hover:underline mt-8" data-testid="blog-post-external">
              Read the original on Substack <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}

          <div className="mt-12 border-t border-slate-100 dark:border-slate-800 pt-8">
            <p className="font-heading text-sm font-bold text-slate-900 dark:text-white mb-3">Keep reading</p>
            <div className="flex flex-wrap gap-2" data-testid="blog-post-internal-links">
              {[
                { to: "/guides", label: "Free VCE text guides" },
                { to: "/vce-english-tutoring-melbourne", label: "VCE English tutoring" },
                { to: "/vce-english-small-group-classes", label: "Small group classes" },
                { to: "/year-12-english-tutoring", label: "Year 12 English" },
                { to: "/blog", label: "All articles" },
              ].map((l) => (
                <Link key={l.to} to={l.to}
                  className="rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-body text-brand-purple dark:text-brand-purple-light hover:border-brand-purple transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-10 rounded-3xl bg-gradient-to-br from-brand-purple to-[#4a1e66] p-10 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
              Want this taught to you properly?
            </h2>
            <p className="text-white/80 font-body mb-6">Small group English classes — hybrid model: online or in-person — with tutors who scored at the very top.</p>
            <Link to="/enrol">
              <Button className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full px-8 py-6 font-bold font-body" data-testid="blog-post-cta">
                Enrol For {settings.term_label} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
